import { useMemo, useState } from "react";

import {
  Download,
  FileBarChart2,
  Package,
  RefreshCcw,
  Server,
  Settings2,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  exportHostsCsv,
  exportSoftwareCsv,
} from "@/features/servers/components/software-inventory-report/csv";
import {
  HostsTable,
  SoftwareTable,
} from "@/features/servers/components/software-inventory-report/tables";
import { StatCard } from "@/features/servers/components/software-inventory-report/ui";
import {
  useHostVerificationInventory,
  useSoftwareInventory,
} from "@/features/servers/hooks/useCenterInfra";
import { useInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import { useSoftwareTypes } from "@/features/servers/hooks/useSoftwareTypes";
import {
  DEFAULT_OS_SUPPORT_POLICY,
  DEFAULT_REVISION_INTERVAL_MONTHS,
} from "@/features/servers/schemas/servers";
import { isPastRevisionWindow } from "@/features/servers/services/infraSettingsService";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { isVersionOutdated } from "@/features/servers/utils/semver";
import { cn } from "@/shared/lib/utils";

type ReportTab = "software" | "hosts";

const TAB_TRIGGER_CLASS = cn(
  "cursor-pointer gap-1.5 rounded-none border-0 border-b-2 border-transparent px-3 py-2.5",
  "bg-transparent shadow-none text-muted-foreground",
  "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  "dark:data-[state=active]:bg-transparent dark:data-[state=active]:border-b-primary",
  "data-[state=active]:border-b-primary data-[state=active]:text-foreground"
);

/**
 * Reporte global: verificación, versiones objetivo y hosts.
 * La configuración de plataforma vive en /settings/platform.
 */
export function SoftwareInventoryReport() {
  const { data: rows = [], isLoading, isFetching, refetch, error } =
    useSoftwareInventory();
  const {
    data: hostRows = [],
    isLoading: loadingHosts,
    isFetching: fetchingHosts,
    refetch: refetchHosts,
    error: hostsError,
  } = useHostVerificationInventory();
  const { data: softwareTypes = [] } = useSoftwareTypes();
  const { data: settings } = useInfraSettings();
  const { can } = usePermissions();
  const canConfigure = can("servers:settings_edit");

  const intervalMonths =
    settings?.revisionIntervalMonths ?? DEFAULT_REVISION_INTERVAL_MONTHS;
  const osPolicy = settings?.osSupportPolicy ?? DEFAULT_OS_SUPPORT_POLICY;

  const [tab, setTab] = useState<ReportTab>("software");
  const [search, setSearch] = useState("");
  const [softwareFilter, setSoftwareFilter] = useState<string>("all");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [outdatedOnly, setOutdatedOnly] = useState(false);
  const [osUnsupportedOnly, setOsUnsupportedOnly] = useState(false);

  const filteredSoftware = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (softwareFilter !== "all") {
        const type = softwareTypes.find((t) => t.id === softwareFilter);
        const matchesType =
          r.softwareTypeId === softwareFilter ||
          (!!type &&
            (r.softwareTypeId === type.id || r.softwareTypeId === type.key));
        if (!matchesType) return false;
      }
      if (
        pendingOnly &&
        !isPastRevisionWindow(r.verifiedAt, intervalMonths)
      ) {
        return false;
      }
      if (outdatedOnly && !isVersionOutdated(r.version, r.targetVersion)) {
        return false;
      }
      if (!q) return true;
      return (
        r.institutionName.toLowerCase().includes(q) ||
        (r.institutionEmail ?? "").toLowerCase().includes(q) ||
        r.softwareLabel.toLowerCase().includes(q) ||
        (r.version ?? "").toLowerCase().includes(q) ||
        (r.hostName ?? "").toLowerCase().includes(q)
      );
    });
  }, [
    rows,
    search,
    softwareFilter,
    pendingOnly,
    outdatedOnly,
    softwareTypes,
    intervalMonths,
  ]);

  const filteredHosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hostRows.filter((r) => {
      if (pendingOnly && !isPastRevisionWindow(r.verifiedAt, intervalMonths)) {
        return false;
      }
      if (osUnsupportedOnly && r.osSupportStatus !== "unsupported") {
        return false;
      }
      if (!q) return true;
      return (
        r.institutionName.toLowerCase().includes(q) ||
        (r.institutionEmail ?? "").toLowerCase().includes(q) ||
        r.hostName.toLowerCase().includes(q) ||
        (r.hostIp ?? "").toLowerCase().includes(q) ||
        (r.anydeskId ?? "").toLowerCase().includes(q) ||
        (r.verifiedBy ?? "").toLowerCase().includes(q) ||
        (r.os ?? "").toLowerCase().includes(q) ||
        (r.osVersion ?? "").toLowerCase().includes(q)
      );
    });
  }, [hostRows, search, pendingOnly, osUnsupportedOnly, intervalMonths]);

  const softwareStats = useMemo(() => {
    const pending = filteredSoftware.filter((r) =>
      isPastRevisionWindow(r.verifiedAt, intervalMonths)
    ).length;
    const outdated = filteredSoftware.filter((r) =>
      isVersionOutdated(r.version, r.targetVersion)
    ).length;
    return {
      outdated,
      pending,
      total: filteredSoftware.length,
    };
  }, [filteredSoftware, intervalMonths]);

  const hostStats = useMemo(() => {
    const pending = filteredHosts.filter((r) =>
      isPastRevisionWindow(r.verifiedAt, intervalMonths)
    ).length;
    const never = filteredHosts.filter((r) => !r.verifiedAt).length;
    const unsupported = filteredHosts.filter(
      (r) => r.osSupportStatus === "unsupported"
    ).length;
    return { pending, never, unsupported, total: filteredHosts.length };
  }, [filteredHosts, intervalMonths]);

  const handleRefresh = () => {
    void refetch();
    void refetchHosts();
  };

  const handleExport = () => {
    if (tab === "software") {
      exportSoftwareCsv(filteredSoftware, intervalMonths);
    } else {
      exportHostsCsv(filteredHosts, intervalMonths);
    }
  };

  const isBusy = isFetching || fetchingHosts;
  const exportDisabled =
    tab === "software"
      ? filteredSoftware.length === 0
      : filteredHosts.length === 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <FileBarChart2 className="h-6 w-6" aria-hidden />
            Inventario de infraestructura
          </h1>
          <p className="text-sm text-muted-foreground">
            Verificación cada {intervalMonths} meses · SO recomendado{" "}
            <span className="font-mono">{osPolicy.recommended}</span> · mínimo{" "}
            <span className="font-mono">{osPolicy.minSupported}</span>
            {canConfigure ? (
              <>
                {" · "}
                <Link
                  to="/settings/platform"
                  className="text-primary hover:underline cursor-pointer"
                >
                  Editar en Configuración
                </Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canConfigure && (
            <Button variant="outline" size="sm" className="cursor-pointer" asChild>
              <Link to="/settings/platform">
                <Settings2 className="h-4 w-4 mr-1.5" aria-hidden />
                Configuración
              </Link>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={handleRefresh}
            disabled={isBusy}
          >
            <RefreshCcw
              className={`h-4 w-4 mr-1.5 ${isBusy ? "animate-spin" : ""}`}
              aria-hidden
            />
            Actualizar
          </Button>
          <Button
            size="sm"
            className="cursor-pointer"
            onClick={handleExport}
            disabled={exportDisabled}
          >
            <Download className="h-4 w-4 mr-1.5" aria-hidden />
            Exportar CSV
          </Button>
        </div>
      </header>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as ReportTab)}
        className="w-full gap-4"
      >
        <div className="border-b border-border">
          <TabsList
            className="h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0"
            aria-label="Tipo de inventario"
          >
            <TabsTrigger value="software" className={TAB_TRIGGER_CLASS}>
              <Package className="size-4" aria-hidden />
              Software
            </TabsTrigger>
            <TabsTrigger value="hosts" className={TAB_TRIGGER_CLASS}>
              <Server className="size-4" aria-hidden />
              Hosts
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="software" className="mt-0 space-y-3 outline-none">
          <div className="grid gap-2 sm:grid-cols-3">
            <StatCard label="Filas" value={softwareStats.total} />
            <StatCard
              label="Desactualizados"
              value={softwareStats.outdated}
              tone={softwareStats.outdated > 0 ? "danger" : "ok"}
            />
            <StatCard
              label={`Verif. pendientes (+${intervalMonths}m)`}
              value={softwareStats.pending}
              tone={softwareStats.pending > 0 ? "warn" : "ok"}
            />
          </div>
        </TabsContent>

        <TabsContent value="hosts" className="mt-0 space-y-3 outline-none">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Hosts" value={hostStats.total} />
            <StatCard
              label="SO sin soporte"
              value={hostStats.unsupported}
              tone={hostStats.unsupported > 0 ? "danger" : "ok"}
            />
            <StatCard
              label="Nunca verificados"
              value={hostStats.never}
              tone={hostStats.never > 0 ? "warn" : "ok"}
            />
            <StatCard
              label={`Pendientes (+${intervalMonths}m)`}
              value={hostStats.pending}
              tone={hostStats.pending > 0 ? "warn" : "ok"}
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap rounded-lg border bg-muted/10 p-2.5">
        <Input
          placeholder={
            tab === "software"
              ? "Buscar centro, software, versión…"
              : "Buscar centro, host, SO, AnyDesk…"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm bg-background"
          aria-label="Buscar en inventario"
        />
        {tab === "software" && (
          <Select value={softwareFilter} onValueChange={setSoftwareFilter}>
            <SelectTrigger className="w-[200px] bg-background">
              <SelectValue placeholder="Software" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los software</SelectItem>
              {softwareTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant={pendingOnly ? "default" : "outline"}
          size="sm"
          className="cursor-pointer"
          onClick={() => setPendingOnly((v) => !v)}
        >
          Solo verif. pendiente
        </Button>
        {tab === "software" ? (
          <Button
            variant={outdatedOnly ? "default" : "outline"}
            size="sm"
            className="cursor-pointer"
            onClick={() => setOutdatedOnly((v) => !v)}
          >
            Solo desactualizados
          </Button>
        ) : (
          <Button
            variant={osUnsupportedOnly ? "default" : "outline"}
            size="sm"
            className="cursor-pointer"
            onClick={() => setOsUnsupportedOnly((v) => !v)}
          >
            Solo SO sin soporte
          </Button>
        )}
      </div>

      {(error || hostsError) && (
        <p className="text-sm text-destructive" role="alert">
          {(error ?? hostsError) instanceof Error
            ? (error ?? hostsError)!.message
            : "Error al cargar inventario"}
        </p>
      )}

      {tab === "software" ? (
        <SoftwareTable
          loading={isLoading}
          rows={filteredSoftware}
          intervalMonths={intervalMonths}
        />
      ) : (
        <HostsTable
          loading={loadingHosts}
          rows={filteredHosts}
          intervalMonths={intervalMonths}
        />
      )}
    </div>
  );
}
